import React, { useEffect, useState, useMemo, useRef } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { downloadFile } from "@/lib/download";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BookOpen, Search, Download, FileSpreadsheet, FileText, Presentation, Plus, Pencil, Trash2, UploadCloud, File } from "lucide-react";
import { toast } from "sonner";

const icons = { Excel: FileSpreadsheet, Word: FileText, PDF: File, PowerPoint: Presentation };
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";
const empty = { title: "", description: "", filename: "", file_data: "" };

const readFile = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export default function Resources() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/resources").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((r) => !q || (r.title || "").includes(q) || (r.description || "").includes(q)), [items, q]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const dl = async (r) => {
    try { await downloadFile(`/resources/${r.id}/download`, r.filename || r.title); toast.success("جارٍ التحميل"); }
    catch { toast.error("تعذر التحميل"); }
  };

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ title: r.title, description: r.description || "", filename: "", file_data: "" }); setEditId(r.id); setOpen(true); };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("حجم الملف يجب أن يكون أقل من 8 ميجا");
    const data = await readFile(f);
    setForm((prev) => ({ ...prev, filename: f.name, file_data: data, title: prev.title || f.name.replace(/\.[^.]+$/, "") }));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("عنوان المورد مطلوب");
    if (!editId && !form.file_data) return toast.error("أرفق ملف المورد (Excel / Word / PDF)");
    setSaving(true);
    try {
      if (editId) await api.put(`/resources/${editId}`, form);
      else await api.post("/resources", form);
      toast.success(editId ? "تم تحديث المورد" : "تمت إضافة المورد — شكراً لإثراء مكتبة منسقينا 🌟");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/resources/${delId}`); toast.success("تم الحذف"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <InspireBanner />

      <PageTitle
        title="مكتبة الموارد والنماذج"
        subtitle={isAdmin ? "أضف وأدر النماذج والأدلة (Excel / Word / PDF) لتمكين منسقيك" : "نماذج وأدلة جاهزة — حمّلها لتؤدي عملك بدقة ومصداقية 🌟"}
        icon={BookOpen}
        actions={isAdmin && <button data-testid="add-resource-btn" onClick={openNew} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> إضافة مورد</button>}
      />

      <div className="glass rounded-2xl p-4 mb-5 relative max-w-md">
        <Search className="absolute right-6 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
        <input data-testid="resource-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن مورد..." className="w-full pr-10 px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400" />
      </div>

      {!filtered.length ? <EmptyState text={isAdmin ? "لا توجد موارد بعد — أضف أول نموذج" : "لا توجد موارد متاحة حالياً"} icon={BookOpen} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((r) => {
            const Icon = icons[r.type] || FileText;
            return (
              <div key={r.id} data-testid={`resource-${r.id}`} className="glass glass-hover rounded-2xl p-5 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0"><Icon size={26} className="text-amber-600" /></div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-head font-bold text-stone-800 truncate">{r.title}</h4>
                  <p className="text-sm text-stone-500 mt-1 line-clamp-2">{r.description || "—"}</p>
                  <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-700">{r.type}</span>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button data-testid={`download-${r.id}`} onClick={() => dl(r)} className="btn-gold p-2.5 rounded-xl" title="تحميل"><Download size={18} /></button>
                  {isAdmin && <button data-testid={`edit-resource-${r.id}`} onClick={() => openEdit(r)} className="p-2.5 rounded-xl hover:bg-amber-500/20 text-amber-600" title="تعديل"><Pencil size={16} /></button>}
                  {isAdmin && <button data-testid={`del-resource-${r.id}`} onClick={() => setDelId(r.id)} className="p-2.5 rounded-xl hover:bg-red-500/20 text-red-700" title="حذف"><Trash2 size={16} /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">{editId ? "تعديل المورد" : "إضافة مورد جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm text-stone-500 mb-1 block">عنوان المورد</label><input data-testid="resource-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></div>
            <div><label className="text-sm text-stone-500 mb-1 block">الوصف</label><textarea data-testid="resource-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls} /></div>
            <div>
              <label className="text-sm text-stone-500 mb-1 block">{editId ? "استبدال الملف (اختياري)" : "ملف المورد (Excel / Word / PDF / PowerPoint)"}</label>
              <div onClick={() => fileRef.current?.click()} className="rounded-xl border-2 border-dashed border-amber-400/40 p-4 text-center cursor-pointer hover:bg-amber-50/60 transition">
                <UploadCloud size={26} className="mx-auto text-sky-600 mb-1" />
                <p className="text-xs text-stone-600">{form.filename || "اضغط لاختيار ملف"}</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.docx,.doc,.pdf,.pptx,.ppt" hidden onChange={onFile} data-testid="resource-file" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="glass glass-hover px-5 py-2 rounded-xl text-stone-700">إلغاء</button>
            <button data-testid="save-resource-btn" onClick={save} disabled={saving} className="btn-gold px-6 py-2 rounded-xl disabled:opacity-50">{saving ? "جارٍ الحفظ..." : "حفظ"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle>حذف المورد</AlertDialogTitle><AlertDialogDescription className="text-stone-500">هل أنت متأكد من حذف هذا المورد؟ لا يمكن التراجع.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="glass text-stone-700">إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-del-resource" onClick={remove} className="bg-red-600 text-white hover:bg-red-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
